// 강연자 API 관련 타입 정의
export interface Lecturer {
  id: number;
  name: string;
  introduction: string;
  image_url: string;
  tags?: string[];
  department?: string;
}

// 강연자 상세 정보 타입
export interface LecturerDetail {
  lecturer_id: number;
  name: string;
  department: string;
  banner_image_url: string;
  introduction: string;
  tags: string[];
}

export interface LecturerDetailResponse {
  code: number;
  status: string;
  message: string;
  data: LecturerDetail;
}

export interface LecturerListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    content: Lecturer[];
    pageable: {
      pageNumber: number;
      pageSize: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
    totalElements: number;
    totalPages: number;
    last: boolean;
    size: number;
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
}

export interface LecturerListParams {
  page?: number;
  size?: number;
}

// 강연자 목록 조회 API
export const fetchLecturers = async (params: LecturerListParams = {}): Promise<LecturerListResponse> => {
  const { page = 0, size = 4 } = params;
  
  const url = `/api/persons/list/mentors?page=${page}&size=${size}`;

  try {
    /// console.log('강연자 목록 조회 요청:', url);
    
    const { apiClient } = await import('.');
    const { data } = await apiClient.get<any>(url, { withCredentials: true });
    /// console.log('강연자 목록 조회 응답 object:', {data});
    
    // API 응답의 department 필드를 그대로 사용 (매핑 로직 제거)
    
    return data as LecturerListResponse;
  } catch (error) {
    /// console.error('강연자 목록 조회 실패:', error);
    throw error;
  }
};

// 강연자 상세 정보 조회 API
export const fetchLecturerDetail = async (lecturerId: string | number): Promise<LecturerDetailResponse> => {
  const url = `/api/persons/mentors/${lecturerId}`;

  try {
    /// console.log('강연자 상세 정보 요청:', url);
    
    const { apiClient } = await import('.');
    const { data } = await apiClient.get<LecturerDetailResponse>(url, { withCredentials: true });
    /// console.log('강연자 상세 정보 응답:', data);
    
    return data;
  } catch (error) {
    /// console.error('강연자 상세 정보 조회 실패:', error);
    throw error;
  }
};

// 카테고리 목록 조회 API
export interface LectureCategory {
  id: number;
  name: string;
  route_name: string;
  level: number;
  parent_category_id: number | null;
  image_url: string;
  optimized_image_url: string;
}

export interface LectureCategoryResponse {
  code: number;
  message: string;
  data: LectureCategory[];
}

export const getLectureCategories = async (): Promise<LectureCategoryResponse> => {
  try {
    const url = '/api/lecture-categories/list';
    /// console.log('카테고리 목록 요청:', url);
    
    const { apiClient } = await import('.');
    const { data } = await apiClient.get<LectureCategoryResponse>(url, { withCredentials: true });
    /// console.log('카테고리 목록 응답:', data);
    
    return data;
  } catch (error) {
    /// console.error('카테고리 목록 조회 실패:', error);
    throw error;
  }
};

// 강의 목록 조회 API
export interface LectureInfo {
  id: number;
  title: string;
  thumbnail_url: string;
  tags: string[];
}

export interface LecturerInfo {
  id: number;
  name: string;
  introduction: string;
  image_url: string;
  tags: string[];
  department?: string;
}

export interface LectureItem {
  lecture_info: LectureInfo;
  lecturer_info: LecturerInfo;
}

export interface LectureListData {
  content: LectureItem[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    unpaged: boolean;
    paged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export interface LectureListResponse {
  code: number;
  status: string;
  message: string;
  data: LectureListData;
}

export const getLecturesByCategory = async (categoryRouteName: string): Promise<LectureListResponse> => {
  try {
    const url = `/api/lectures/info/list/filtered?categoryRouteName=${encodeURIComponent(categoryRouteName)}`;
    /// console.log('강의 목록 요청:', url);
    
    const { apiClient } = await import('.');
    const { data } = await apiClient.get<LectureListResponse>(url, { withCredentials: true });
    /// console.log('강의 목록 응답:', data);
    
    return data;
  } catch (error) {
    /// console.error('강의 목록 조회 실패:', error);
    throw error;
  }
};

// 챕터 목록 조회 API
export interface LectureChapter {
  uri: string;
  title: string;
  timecode: number;
  thumbnails: any[];
}

export interface ChapterListResponse {
  code: number;
  status: string;
  message: string;
  data: LectureChapter[];
}

// 커피챗 멘토 타입 정의
export interface CoffeeChatMentor {
  id: number;
  name: string;
  email: string;
  department: string;
  tags: string[];
  image_url: string;
  banner_url: string;
  introduction: string;
  is_temp: boolean;
  created_at: string;
}

export interface CoffeeChatMentorListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: CoffeeChatMentor[];
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    pageable: {
      offset: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      paged: boolean;
      pageNumber: number;
      pageSize: number;
      unpaged: boolean;
    };
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

export interface CoffeeChatMentorListParams {
  isTemp?: boolean;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

// 커피챗 멘토 목록 조회 API
export const getCoffeeChatMentors = async (params: CoffeeChatMentorListParams = {}): Promise<CoffeeChatMentorListResponse> => {
  const { isTemp = false, from, to, page = 0, size = 100 } = params;
  
  const queryParams = new URLSearchParams({
    isTemp: isTemp.toString(),
    page: page.toString(),
    size: size.toString()
  });
  
  if (from) queryParams.append('from', from);
  if (to) queryParams.append('to', to);
  
  const url = `/api/admin/mentors/list?${queryParams.toString()}`;

  try {
    /// console.log('커피챗 멘토 목록 조회 요청:', url);
    
    const { apiClient } = await import('.');
    const { data } = await apiClient.get<CoffeeChatMentorListResponse>(url, { withCredentials: true });
    /// console.log('커피챗 멘토 목록 조회 응답:', data);
    
    return data;
  } catch (error) {
    /// console.error('커피챗 멘토 목록 조회 실패:', error);
    throw error;
  }
};

export const getLectureChapters = async (lectureId: number): Promise<ChapterListResponse> => {
  try {
    const url = `/api/lectures/info/${lectureId}/chapters`;
    /// console.log('챕터 목록 요청:', url);
    
    const { apiClient } = await import('.');
    const { data } = await apiClient.get<ChapterListResponse>(url, { withCredentials: true });
    /// console.log('챕터 목록 응답:', data);
    
    return data;
  } catch (error) {
    /// console.error('챕터 목록 조회 실패:', error);
    throw error;
  }
};
